import { OAuthCallbackHandler } from '@/components/auth/OAuthCallbackHandler';
import {
  AnalyticsPreferencesControl,
  AnalyticsPreferencesLink,
} from '@/components/analytics/AnalyticsPreferences';
import { SplitAuthShell } from '@/components/SplitAuthShell';
import { Alert } from '@/components/ui/alert';
import { LoadingPage } from '@/components/ui/loading-page';
import { useAuth } from '@/contexts/auth-context';
import { useBrandSelfSignupStatusQuery } from '@/hooks/queries/use-brand-self-signup-status-query';
import { useBrandSignupInvitationDetails } from '@/hooks/queries/use-brand-signup-invitation-details';
import NavigationHistoryTracker from '@/libs/navigation-history';
import { getLoginReturnPath } from '@/libs/mcp-return-to';
import { OnboardingHeading } from '@/pages/onboarding/_components/OnboardingHeading';
import { ONBOARDING_PATHS } from '@/pages/onboarding/_components/onboarding-steps';
import { Box, Button, Flex, Link, Stack, Text } from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthBrandPanel } from './_components/AuthBrandPanel';
import { oauthAuthError } from './_components/auth-error';
import { AuthTopBarAction } from './_components/AuthTopBarAction';
import { LoginForm } from './_components/LoginForm';
import { SignupPanel } from './_components/SignupPanel';

type AuthPageMode = 'login' | 'validating-invitation' | 'invitation-signup' | 'self-signup';

const HEADINGS: Record<AuthPageMode, { title: string; subtitle: string }> = {
  login: { title: 'authPage.loginHeading', subtitle: 'authPage.loginSubtext' },
  'validating-invitation': {
    title: 'authPage.validatingInvitationHeading',
    subtitle: 'authPage.validatingInvitationSubtext',
  },
  'invitation-signup': { title: 'authPage.signupHeading', subtitle: 'authPage.signupSubtext' },
  'self-signup': { title: 'authPage.selfSignupHeading', subtitle: 'authPage.selfSignupSubtext' },
};

interface AuthPageProps {
  /**
   * `signup` is the brand self sign-up (`/signup`), shown while the switch is
   * open and sent back to the login page while it is closed. The login page
   * also carries the sign-up of a brand an admin invited (`?mode=signup&token=`).
   */
  entry?: 'login' | 'signup';
}

const AuthPage = ({ entry = 'login' }: AuthPageProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selfSignupStatus = useBrandSelfSignupStatusQuery();
  const selfSignupOpen = selfSignupStatus.data?.enabled;

  const requestedInvitationSignup = entry === 'login' && searchParams.get('mode') === 'signup';
  const signupToken = requestedInvitationSignup ? searchParams.get('token') : null;
  const invitationQuery = useBrandSignupInvitationDetails(signupToken);
  const invitationDetails = invitationQuery.data;
  const isInvitationSignup =
    requestedInvitationSignup && !!signupToken && !!invitationDetails && !invitationQuery.isError;
  const isValidatingInvitation =
    requestedInvitationSignup && !!signupToken && invitationQuery.isLoading;
  const invitationErrorMessage =
    requestedInvitationSignup && !isInvitationSignup && !isValidatingInvitation
      ? !signupToken
        ? t('authPage.signupInvitationMissing')
        : t('authPage.signupInvitationInvalid')
      : null;

  const mode: AuthPageMode =
    entry === 'signup'
      ? 'self-signup'
      : isValidatingInvitation
        ? 'validating-invitation'
        : isInvitationSignup
          ? 'invitation-signup'
          : 'login';

  // Redirect authenticated users (covers every mode)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(getLoginReturnPath(searchParams));
    }
  }, [isAuthenticated, navigate, searchParams]);

  const oauthError = useMemo(() => oauthAuthError(searchParams.get('error')), [searchParams]);

  const returnToLogin = useCallback(() => {
    if (entry === 'signup') {
      navigate('/');
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('mode');
    newParams.delete('token');
    newParams.delete('tab');
    setSearchParams(newParams, { replace: true });
  }, [entry, navigate, searchParams, setSearchParams]);

  const startInvitedOnboarding = useCallback(async () => {
    await refreshCurrentUser();
    navigate('/onboarding/shopify');
  }, [refreshCurrentUser, navigate]);

  // While the brand gives back the code sent to its address, the code screen
  // carries the page's only heading.
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  const startSelfSignupOnboarding = useCallback(async () => {
    await refreshCurrentUser();
    navigate(ONBOARDING_PATHS.brand);
  }, [refreshCurrentUser, navigate]);

  if (entry === 'signup' && selfSignupOpen === false) {
    return <Navigate to="/" replace />;
  }

  // The switch is read before the sign-up is drawn, so a closed sign-up never flashes.
  if (entry === 'signup' && selfSignupOpen === undefined) {
    return <LoadingPage />;
  }

  const heading = HEADINGS[mode];
  const subtitle =
    mode === 'self-signup' && selfSignupStatus.data?.cardRequiredOutsideShopify
      ? 'authPage.selfSignupSubtextWithCard'
      : heading.subtitle;
  const isSignup = mode === 'self-signup' || mode === 'invitation-signup';

  return (
    <>
      <NavigationHistoryTracker />
      <OAuthCallbackHandler />

      <SplitAuthShell
        maxW="392px"
        py={{ base: 5, md: '34px' }}
        brandPanel={<AuthBrandPanel />}
        topBar={
          <Flex align="center" justify="space-between" gap={3}>
            <Logo h={{ base: '20px', md: '23px' }} flexShrink={0} />
            {mode !== 'self-signup' && <AuthTopBarAction selfSignupOpen={selfSignupOpen} />}
          </Flex>
        }
        footer={
          <Stack as="footer" gap={4} w="full" maxW="392px" alignSelf="center">
            {isSignup ? <AnalyticsPreferencesControl placement="inline" /> : null}
            <Text fontSize="12px" color="gray.500">
              © {new Date().getFullYear()} Fluenceur ·{' '}
              <Link href="https://www.fluenceur.com/terms-and-conditions" color="gray.500">
                {t('signup.termsLink')}
              </Link>{' '}
              ·{' '}
              <Link href="https://www.fluenceur.com/privacy-policy" color="gray.500">
                {t('signup.privacyLink')}
              </Link>
              <AnalyticsPreferencesLink />
            </Text>
          </Stack>
        }
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 7 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
          >
            {!verifyingEmail && (
              <Box mb={{ base: '22px', md: '26px' }}>
                <OnboardingHeading title={t(heading.title)} subtitle={t(subtitle)} />
              </Box>
            )}

            {mode === 'validating-invitation' && (
              <Text fontSize="14px" color="gray.600">
                {t('authPage.validatingInvitationSubtext')}
              </Text>
            )}
            {mode === 'self-signup' && (
              <SignupPanel
                oauthError={oauthError}
                onCreated={startSelfSignupOnboarding}
                onVerifyingChange={setVerifyingEmail}
              />
            )}
            {mode === 'invitation-signup' && signupToken && (
              <SignupPanel
                oauthError={oauthError}
                invitationToken={signupToken}
                onCreated={startInvitedOnboarding}
              />
            )}
            {mode === 'login' && (
              <Stack gap={4}>
                {invitationErrorMessage && (
                  <Alert status="error" title={t('common.error')} textAlign="center">
                    {invitationErrorMessage}
                  </Alert>
                )}
                <LoginForm oauthError={oauthError} />
              </Stack>
            )}
          </motion.div>
        </AnimatePresence>

        {isSignup && !verifyingEmail && (
          <Flex
            justify="center"
            align="baseline"
            mt={{ base: '22px', md: '26px' }}
            fontSize="13.5px"
            color="gray.600"
            gap="6px"
          >
            <Text as="span">{t('authPage.footerHaveAccount')}</Text>
            <Button
              variant="plain"
              h="auto"
              minW={0}
              p={0}
              borderRadius={0}
              borderBottomWidth="1px"
              borderColor="border.emphasized"
              fontFamily="heading"
              fontSize="13.5px"
              fontWeight="600"
              color="primary.900"
              onClick={returnToLogin}
            >
              {t('authPage.footerLogin')}
            </Button>
          </Flex>
        )}
      </SplitAuthShell>
    </>
  );
};

export default AuthPage;
